<template>
    <div class="admin-container">
        <!-- Stats Section -->
        <div class="stats-section">
            <div class="stat-card">
                <div class="stat-icon-wrapper">
                    <i class="fas fa-users"></i>
                </div>
                <div class="stat-info">
                    <span class="stat-label">Total Users</span>
                    <span class="stat-value">{{ stats.totalUsers }}</span>
                    <div class="stat-trend positive">
                        <i class="fas fa-arrow-up"></i>
                        <span>{{ stats.usersChange }}% this week. ({{ stats.userChangeCount }} new.)</span>
                    </div>
                </div>
            </div>

            <div class="stat-card">
                <div class="stat-icon-wrapper green">
                    <i class="fas fa-user-check"></i>
                </div>
                <div class="stat-info">
                    <span class="stat-label">Active Users</span>
                    <span class="stat-value">{{ stats.activeUsers }}</span>
                    <div class="stat-trend positive">
                        <i class="fas fa-arrow-up"></i>
                        <span>8% this week</span>
                    </div>
                </div>
            </div>

            <div class="stat-card">
                <div class="stat-icon-wrapper red">
                    <i class="fas fa-user-slash"></i>
                </div>
                <div class="stat-info">
                    <span class="stat-label">Banned Users</span>
                    <span class="stat-value">{{ stats.bannedUsers }}</span>
                </div>
            </div>

            <div class="stat-card">
                <div class="stat-icon-wrapper purple">
                    <i class="fas fa-dollar-sign"></i>
                </div>
                <div class="stat-info">
                    <span class="stat-label">Total Balance</span>
                    <span class="stat-value">${{ Number(stats.totalBalance).toFixed(2) }}</span>
                    <div class="stat-trend positive">
                        <i class="fas fa-arrow-up"></i>
                        <span>15% this month</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Search and Filter Section -->
        <div class="control-section">
            <div class="search-box">
                <i class="fas fa-search search-icon"></i>
                <input 
                    v-model="searchQuery" 
                    type="text" 
                    placeholder="Search by username, email or Steam ID..."
                >
            </div>
            
            <div class="filter-controls">
                <div class="filter-group">
                    <label>Status</label>
                    <select v-model="statusFilter" @change="handleSearch">
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="banned">Banned</option>
                    </select>
                </div>
                
                <div class="filter-group">
                    <label>Sort By</label>
                    <select v-model="sortBy" @change="handleSort($event.target.value)">
                        <option value="joined_at_desc">Newest First</option>
                        <option value="joined_at_asc">Oldest First</option>
                        <option value="user_name_asc">Name A-Z</option>
                        <option value="user_name_desc">Name Z-A</option>
                        <option value="balance_desc">Balance (High to Low)</option>
                        <option value="balance_asc">Balance (Low to High)</option>
                    </select>
                </div>
            </div>
        </div>

        <!-- Users Table -->
        <div class="table-wrapper">
            <table class="users-table">
                <thead>
                    <tr>
                        <th>User</th>
                        <th>Steam ID</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Balance</th>
                        <th>Join Date</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="user in users" :key="user.id" class="user-row">
                        <td class="user-cell">
                            <img :src="user.avatar_medium || defaultAvatar" :alt="user.user_name" class="user-avatar">
                            <div class="user-info">
                                <span class="username">{{ user.user_name }}</span>
                                <span class="user-id">#{{ user.id }}</span>
                            </div>
                        </td>
                        <td class="text-white">{{ user.steam_id }}</td>
                        <td class="text-white">{{ user.mail || 'Not Set' }}</td>
                        <td>
                            <span :class="['role-badge', user.ROLE.toLowerCase()]">
                                {{ user.ROLE }}
                            </span>
                        </td>
                        <td>
                            <span :class="['status-badge', getUserStatus(user.profile_state)]">
                                {{ getUserStatus(user.profile_state) }}
                            </span>
                        </td>
                        <td class="balance text-white">${{ Number(user.balance).toFixed(2) }}</td>
                        <td class="date text-white">{{ formatDate(user.joined_at) }}</td>
                        <td class="actions-cell">
                            <button class="action-btn edit" @click="editUser(user)" title="Edit User">
                                <i class="fas fa-edit"></i>
                            </button>
                            <button class="action-btn view" @click="viewDetails(user)" title="View Steam Profile">
                                <i class="fab fa-steam"></i>
                            </button>
                            <button class="action-btn delete" @click="confirmDelete(user)" title="Delete User">
                                <i class="fas fa-trash"></i>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <div class="pagination">
            <button :disabled="currentPage === 1" @click="changePage(currentPage - 1)" class="page-btn">
                <i class="fas fa-chevron-left"></i>
                Previous
            </button>

            <div class="page-info">
                <span>Page {{ currentPage }} of {{ totalPages }}</span>
                <span class="total-items">Total: {{ totalUsers }} users</span>
            </div>

            <button :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)" class="page-btn">
                Next
                <i class="fas fa-chevron-right"></i>
            </button>
        </div>

        <!-- Edit Modal -->
        <div v-if="showEditModal" class="modal-overlay">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="text-white">Edit User</h5>
                    <button class="close-btn" @click="closeEditModal">&times;</button>
                </div>
                <div class="modal-body">
                    <form @submit.prevent="saveUser">
                        <div class="form-group">
                            <label>Username</label>
                            <input v-model="editForm.user_name" type="text" class="form-control" required>
                        </div>
                        <div class="form-group">
                            <label>Steam ID</label>
                            <input v-model="editForm.steam_id" type="text" class="form-control" required>
                        </div>
                        <div class="form-group">
                            <label>Email</label>
                            <input v-model="editForm.mail" type="email" class="form-control">
                        </div>
                        <div class="form-group">
                            <label>Trade Link</label>
                            <input v-model="editForm.trade_link" type="text" class="form-control">
                        </div>
                        <div class="form-group">
                            <label>Profile URL</label>
                            <input v-model="editForm.profile_url" type="text" class="form-control">
                        </div>
                        <div class="form-group">
                            <label>Avatar URL</label>
                            <input v-model="editForm.avatar_medium" type="text" class="form-control">
                        </div>
                        <div class="form-group">
                            <label>Country</label>
                            <input v-model="editForm.country" type="text" class="form-control">
                        </div>
                        <div class="form-group">
                            <label>Steam Account Created</label>
                            <input v-model="editForm.steam_account_created" type="number" class="form-control">
                        </div>
                        <div class="form-group">
                            <label>Avatar Hash</label>
                            <input v-model="editForm.avatar_hash" type="text" class="form-control">
                        </div>
                        <div class="form-group">
                            <label>Balance</label>
                            <input v-model="editForm.balance" type="number" step="0.01" class="form-control">
                        </div>
                        <div class="form-group">
                            <label>Role</label>
                            <select v-model="editForm.ROLE" class="form-control" required>
                                <option value="USER">User</option>
                                <option value="ADMIN">Admin</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Status</label>
                            <select v-model="editForm.profile_state" class="form-control" required>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                                <option value="2">Banned</option>
                            </select>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn-pinko btn-pink" @click="closeEditModal">Cancel</button>
                            <button type="submit" class="btn-pinko btn-pink">Save Changes</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AdminUserService from '@/services/admin.UserService.js';

export default {
    name: 'UserListView',
    data() {
        return {
            users: [],
            searchQuery: '',
            statusFilter: '',
            sortBy: 'newest',
            currentPage: 1,
            itemsPerPage: 10,
            totalUsers: 0,
            defaultAvatar: 'https://avatars.steamstatic.com/fef49e7fa7e1997310d705b2a6158ff8dc1cdfeb_full.jpg',
            loading: false,
            stats: {
                totalUsers: 0,
                activeUsers: 0,
                bannedUsers: 0,
                totalBalance: 0,
                userChangeCount: 0,
                usersChange: 0
            },
            showEditModal: false,
            editForm: {
                id: null,
                user_name: '',
                steam_id: '',
                ROLE: 'USER',
                mail: '',
                trade_link: '',
                profile_url: '',
                avatar_medium: '',
                country: '',
                steam_account_created: null,
                profile_state: '1',
                avatar_hash: '',
                joined_at: null,
                balance: 0
            }
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.totalUsers / this.itemsPerPage);
        }
    },
    methods: {
        async fetchUsers() {
            try {
                this.loading = true;
                const response = await AdminUserService.getUsers({
                    search: this.searchQuery,
                    sort: this.sortBy,
                    page: this.currentPage,
                    limit: this.itemsPerPage
                });

                this.users = response.items;
                this.totalUsers = response.total;
                await this.fetchStats();
            } catch (error) {
                console.error('Error fetching users:', error);
            } finally {
                this.loading = false;
            }
        },
        async fetchStats() {
            try {
                const stats = await AdminUserService.getStats();
                this.stats = stats;
            } catch (error) {
                console.error('Error fetching stats:', error);
            }
        },
        formatDate(date) {
            return new Date(date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        },
        getUserStatus(profileState) {
            switch (profileState) {
                case 1:
                    return 'active'
                case 0:
                    return 'inactive'
                case 2:
                    return 'banned'
                default:
                    return 'unknown'
            }
        },
        editUser(user) {
            this.editForm = {
                id: user.id,
                user_name: user.user_name,
                steam_id: user.steam_id,
                ROLE: user.ROLE,
                mail: user.mail || '',
                trade_link: user.trade_link || '',
                profile_url: user.profile_url || '',
                avatar_medium: user.avatar_medium || '',
                country: user.country || '',
                steam_account_created: user.steam_account_created,
                profile_state: user.profile_state.toString(),
                avatar_hash: user.avatar_hash || '',
                joined_at: user.joined_at,
                balance: user.balance
            };
            this.showEditModal = true;
        },
        closeEditModal() {
            this.showEditModal = false;
            this.editForm = {
                id: null,
                user_name: '',
                steam_id: '',
                ROLE: 'USER',
                mail: '',
                trade_link: '',
                profile_url: '',
                avatar_medium: '',
                country: '',
                steam_account_created: null,
                profile_state: '1',
                avatar_hash: '',
                joined_at: null,
                balance: 0
            };
        },
        async saveUser() {
            try {
                const response = await AdminUserService.updateUser(this.editForm);
                if (response) {
                    await this.fetchUsers();
                    this.closeEditModal();
                }
            } catch (error) {
                console.error('Error updating user:', error);
            }
        },
        viewDetails(user) {
            window.open(user.profile_url, '_blank');
        },
        async confirmDelete(user) {
            if (confirm(`Are you sure you want to delete user ${user.user_name}?`)) {
                try {
                    //await AdminUserService.deleteUser(user.id);
                    await this.fetchUsers();
                } catch (error) {
                    console.error('Error deleting user:', error);
                }
            }
        },
        async changePage(page) {
            this.currentPage = page;
            await this.fetchUsers();
        },
        async handleSearch() {
            this.currentPage = 1;
            await this.fetchUsers();
        },
        async handleSort(value) {
            this.sortBy = value;
            await this.fetchUsers();
        }
    },
    created() {
        this.fetchUsers();
    },
    watch: {
        searchQuery: {
            handler: debounce(function () {
                this.handleSearch();
            }, 300),
            immediate: false
        }
    }
}

// Debounce fonksiyonu
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}
</script>

<style scoped>

/* Stats Section */
.stats-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2.5rem;
}

.stat-card {
    background: linear-gradient(145deg, #222, #2a2a2a);
    border-radius: 16px;
    padding: 1.5rem;
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease;
    min-height: 140px;
}

.stat-card:hover {
    transform: translateY(-2px);
}

.stat-icon-wrapper {
    width: 48px;
    height: 48px;
    background: #2563eb;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    color: white;
    flex-shrink: 0;
}

.stat-icon-wrapper.green {
    background: #059669;
}

.stat-icon-wrapper.red {
    background: #dc2626;
}

.stat-icon-wrapper.purple {
    background: #7c3aed;
}

.stat-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.stat-label {
    color: #999;
    font-size: 0.875rem;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

.stat-value {
    font-size: 2rem;
    font-weight: 600;
    color: #fff;
    line-height: 1.2;
}

.stat-trend {
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    padding: 4px 8px;
    border-radius: 6px;
    width: fit-content;
}

.stat-trend.positive {
    color: #059669;
    background: rgba(5, 150, 105, 0.1);
}

.stat-trend.negative {
    color: #dc2626;
    background: rgba(220, 38, 38, 0.1);
}

.stat-trend i {
    font-size: 0.75rem;
}

/* Control Section */
.control-section {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 1.5rem;
    margin-bottom: 2rem;
    background: linear-gradient(145deg, #1a1a1a, #222);
    border-radius: 16px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.search-box {
    display: flex;
    align-items: center;
    min-width: 300px;
    flex: 1;
    max-width: 600px;
    background: rgba(26, 26, 26, 0.8);
    border-radius: 12px;
    border: 2px solid #333;
    padding: 0.5rem 1rem;
    transition: all 0.2s ease;
}

.search-box:focus-within {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.search-box .search-icon {
    color: #666;
    font-size: 1rem;
    margin-right: 0.75rem;
    transition: color 0.2s ease;
}

.search-box:focus-within .search-icon {
    color: #2563eb;
}

.search-box input {
    flex: 1;
    background: transparent;
    border: none;
    color: #fff;
    font-size: 1rem;
    padding: 0.5rem 0;
    width: 100%;
}

.search-box input:focus {
    outline: none;
}

.search-box input::placeholder {
    color: #666;
}

.filter-controls {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    min-width: 180px;
}

.filter-group label {
    font-size: 0.875rem;
    color: #999;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

.filter-group select {
    padding: 0.875rem 2.5rem 0.875rem 1rem;
    border-radius: 12px;
    border: 2px solid #333;
    background: rgba(26, 26, 26, 0.8);
    color: #fff;
    font-size: 0.95rem;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 1rem center;
    background-size: 1em;
    transition: all 0.2s ease;
    cursor: pointer;
}

.filter-group select:focus {
    border-color: #2563eb;
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

/* Table Styles */
.table-wrapper {
    background: #222;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 2rem;
    min-height: 400px;
    position: relative;
}

.users-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.users-table th {
    background: #2a2a2a;
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: #fff;
}

.users-table td {
    padding: 1rem;
    border-bottom: 1px solid #333;
    color: #fff;
}

.user-row {
    transition: background-color 0.2s ease;
}

.user-row:hover {
    background: #2a2a2a;
}

.user-cell {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    object-fit: cover;
}

.user-info {
    display: flex;
    flex-direction: column;
}

.username {
    font-weight: 500;
    color: #fff;
}

.user-id {
    color: #666;
    font-size: 0.875rem;
}

.role-badge,
.status-badge {
    padding: 0.4rem 0.8rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    display: inline-block;
}

.role-badge.admin {
    background: rgba(124, 58, 237, 0.2);
    color: #7c3aed;
}

.role-badge.user {
    background: rgba(37, 99, 235, 0.2);
    color: #2563eb;
}

.role-badge.moderator {
    background: rgba(5, 150, 105, 0.2);
    color: #059669;
}

.status-badge.active {
    background: rgba(5, 150, 105, 0.2);
    color: #059669;
}

.status-badge.inactive {
    background: rgba(107, 114, 128, 0.2);
    color: #6b7280;
}

.status-badge.banned {
    background: rgba(220, 38, 38, 0.2);
    color: #dc2626;
}

.actions-cell {
    display: flex;
    gap: 0.5rem;
}

.action-btn {
    padding: 0.5rem;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: #666;
    cursor: pointer;
    transition: all 0.2s ease;
}

.action-btn:hover {
    transform: translateY(-1px);
}

.action-btn.edit:hover {
    color: #2563eb;
}

.action-btn.view:hover {
    color: #00adee;
}

.action-btn.delete:hover {
    color: #dc2626;
}

/* Pagination */
.pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    background: #222;
    border-radius: 12px;
}

.page-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    border-radius: 8px;
    border: 1px solid #333;
    background: #1a1a1a;
    color: #fff;
    cursor: pointer;
    transition: all 0.2s ease;
}

.page-btn:not(:disabled):hover {
    background: #2a2a2a;
    border-color: #444;
}

.page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    color: #666;
}

.total-items {
    font-size: 0.875rem;
}

/* Loading State */
.loading-state {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(26, 26, 26, 0.8);
    backdrop-filter: blur(4px);
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
}

.loader {
    width: 40px;
    height: 40px;
    border: 3px solid #333;
    border-radius: 50%;
    border-top-color: #2563eb;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* Responsive Design */
@media (max-width: 1200px) {
    .stats-section {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .stats-section {
        grid-template-columns: 1fr;
    }

    .control-section {
        flex-direction: column;
    }

    .filter-controls {
        flex-direction: column;
        width: 100%;
    }

    .filter-group select {
        width: 100%;
    }

    .users-table {
        display: block;
        overflow-x: auto;
    }
}

.steam-id {
    color: #fff;
}

.text-white {
    color: #fff !important;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.85);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    backdrop-filter: blur(8px);
}

.modal-content {
    background: var(--secondary-color);
    border-radius: 12px;
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    animation: modalFadeIn 0.3s ease-out;
}

.modal-header {
    padding: 15px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 12px 12px 0 0;
}

.modal-body {
    padding: 20px;
}

.modal-footer {
    padding: 15px 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 0 0 12px 12px;
}

.form-group {
    margin-bottom: 15px;
}

.form-group label {
    display: block;
    margin-bottom: 5px;
    color: white;
    font-weight: 500;
}

.form-control {
    width: 100%;
    padding: 10px;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.05);
    color: white;
    transition: all 0.2s ease;
}

.form-control:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
}

.form-control:hover {
    border-color: rgba(255, 255, 255, 0.15);
}

select.form-control {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='white' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    padding-right: 30px;
}

.btn-pinko {
    padding: 10px 20px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s ease;
}

.btn-pinko:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.btn-pink {
    background-color: #8f24c0;
    color: white;
}

.btn-pink:hover {
    background-color: #7a1fa0;
}

.btn-secondary {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
}

.btn-secondary:hover {
    background-color: rgba(255, 255, 255, 0.15);
}

.close-btn {
    background: none;
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
    transition: all 0.2s ease;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
}

.close-btn:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: rotate(90deg);
}

@keyframes modalFadeIn {
    from {
        opacity: 0;
        transform: translateY(-20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>